/**
 * Region configuration and storage helpers for the integration shell at
 * `/integration-shell` and the integration login pages.
 *
 * Mirrors the schema from `haast-integrations/shared/regions.ts` so the same
 * regions JSON can be consumed by the SvelteKit shell, the Apps Script
 * Google Docs sidebar, and the Figma plugin.
 */

export type RegionCode = "AU" | "UK" | "US" | "SIT" | "STAGING" | "LOCAL";

export type Region = {
  code: RegionCode;
  label: string;
  host: string;
};

export type IntegrationSlug =
  | "chrome"
  | "figma"
  | "google-docs"
  | "office"
  | "workfront";

export const INTEGRATION_SLUGS: readonly IntegrationSlug[] = [
  "chrome",
  "figma",
  "google-docs",
  "office",
  "workfront",
] as const;

/**
 * Resolve a raw `?integration=` query value to a known integration slug,
 * falling back to "office" for missing or unrecognised values.
 */
export function parseIntegrationSlug(value: string | null): IntegrationSlug {
  return INTEGRATION_SLUGS.includes(value as IntegrationSlug)
    ? (value as IntegrationSlug)
    : "office";
}

export class InvalidRegionsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidRegionsError";
  }
}

export const REGION_LABELS: Record<RegionCode, string> = {
  AU: "Australia",
  UK: "UK",
  US: "US",
  SIT: "SIT",
  STAGING: "STAGING",
  LOCAL: "LOCAL",
};

export const REGION_CODES: readonly RegionCode[] = [
  "AU",
  "UK",
  "US",
  "SIT",
  "STAGING",
  "LOCAL",
] as const;

export const REGION_STORAGE_KEY = "haast_region";

export function parseRegions(raw: string): Region[] {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch (err) {
    throw new InvalidRegionsError(
      `REGIONS_JSON is not valid JSON: ${(err as Error).message}`,
    );
  }
  if (!Array.isArray(data) || data.length === 0) {
    throw new InvalidRegionsError(
      "REGIONS_JSON must be a non-empty array of regions.",
    );
  }
  const seen = new Set<string>();
  return data.map((entry, idx) => {
    if (typeof entry !== "object" || entry === null) {
      throw new InvalidRegionsError(`Region at index ${idx} is not an object.`);
    }
    const { code, label, host } = entry as Partial<Region>;
    if (typeof code !== "string" || !code.trim()) {
      throw new InvalidRegionsError(`Region at index ${idx} has no "code".`);
    }
    if (!REGION_CODES.includes(code as RegionCode)) {
      throw new InvalidRegionsError(
        `Region "${code}" is not one of ${REGION_CODES.join(", ")}.`,
      );
    }
    if (typeof label !== "string" || !label.trim()) {
      throw new InvalidRegionsError(
        `Region "${code}" at index ${idx} has no "label".`,
      );
    }
    if (typeof host !== "string" || !/^https?:\/\//.test(host)) {
      throw new InvalidRegionsError(
        `Region "${code}" has an invalid "host": must start with http:// or https://`,
      );
    }
    if (seen.has(code)) {
      throw new InvalidRegionsError(`Duplicate region code: "${code}".`);
    }
    seen.add(code);
    return { code: code as RegionCode, label, host };
  });
}

export type EntryDecision =
  | { kind: "picker"; regions: Region[] }
  | { kind: "frame"; region: Region }
  | { kind: "empty" };

export function chooseEntry(
  regions: Region[],
  storedCode: string | null,
): EntryDecision {
  if (regions.length === 0) return { kind: "empty" };
  if (regions.length === 1) return { kind: "frame", region: regions[0] };
  if (storedCode) {
    const match = regions.find((r) => r.code === storedCode);
    if (match) return { kind: "frame", region: match };
  }
  return { kind: "picker", regions };
}

export function regionEntryUrl(
  region: Region,
  integration: IntegrationSlug,
  tabId?: string | null,
): string {
  const url = new URL(region.host);
  /**
   * The Chrome extension has no dedicated `/chrome` route — it loads the
   * generic integrations panel keyed by `?type=chrome`, and that route's
   * layout bounces unauthenticated users to login on its own. So unlike
   * the office/google-docs/figma add-ins, chrome resolves to
   * `/integrations?type=chrome` rather than `/{integration}`.
   *
   * The Chrome side panel forwards the active tab id through the iframe
   * URL so the panel knows which tab to capture; preserve it across the
   * region redirect so capture keeps targeting the right tab.
   */
  if (integration === "chrome") {
    url.pathname = url.pathname.replace(/\/+$/, "") + "/integrations";
    url.searchParams.set("type", "chrome");
    if (tabId) {
      url.searchParams.set("tabId", tabId);
    }
    return url.toString();
  }
  const integrationPath = `/${integration}`;
  const normalizedPathname = url.pathname.replace(/\/+$/, "");
  if (!normalizedPathname.endsWith(integrationPath)) {
    url.pathname = normalizedPathname + integrationPath;
  }
  return url.toString();
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadStoredRegion(): string | null {
  const storage = safeLocalStorage();
  if (!storage) return null;
  try {
    return storage.getItem(REGION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeRegion(code: string): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(REGION_STORAGE_KEY, code);
  } catch {
    /* no-op: private mode, quota, etc. */
  }
}

export function clearStoredRegion(): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(REGION_STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
